import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { alpha, useTheme } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  copyDesignCalendarYear,
  createDesignCalendarAccion,
  createDesignCalendarNota,
  deleteDesignCalendarAccion,
  deleteDesignCalendarNota,
  deleteDesignCalendarYear,
  fetchDesignCalendar,
  updateDesignCalendarAccion,
  updateDesignCalendarCliente,
  updateDesignCalendarNota,
} from "../../api/DesignCalendar/designCalendarApi";
import DesignCalendarDeleteYearDialog from "./DesignCalendarDeleteYearDialog";
import { socket } from "../../socket";
import {
  DESIGN_CALENDAR_CLIENTES_DEFAULT,
  mergeClientesCatalog,
  clienteById,
  entryAccentColor,
  entryFillColor,
  pickerOptionsForValue,
  matchesClienteFilter,
} from "./designCalendarClientes";
import {
  DesignCalendarClientesContext,
  useDesignCalendarClientes,
} from "./DesignCalendarClientesContext";
import DesignCalendarClientesPalette from "./DesignCalendarClientesPalette";
import { isNetworkError, nextTempId } from "./designCalendarOfflinePure";
import useDesignCalendarOffline from "./useDesignCalendarOffline";
import {
  DESIGN_CALENDAR_SEED_YEAR,
  designCalendarSeedAcciones,
  designCalendarSeedNotas,
} from "./designCalendarSeed2026";
import {
  SIMBOLOS_PICKER,
  addDaysIso,
  daysInMonth,
  formatDiaMes,
  groupAccionesByMonth,
  groupNotasByFecha,
  isWeekend,
  layoutMonthNotas,
  monthAccent,
  monthName,
  notaFechaFin,
  notaSimbolos,
  notaSpanLabel,
  todayIso,
  toIsoDate,
  weekdayLetter,
} from "./designCalendarModel";
import {
  DesignCalendarSymbolIcon,
  DesignCalendarSymbolStack,
} from "./DesignCalendarSymbolIcon";

const ACCENT = "#0f766e";

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const rise = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const bob = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

const flyCopy = keyframes`
  0% { opacity: 0.25; transform: translateX(0) scale(0.9); }
  35% { opacity: 1; transform: translateX(22px) scale(1); }
  70% { opacity: 1; transform: translateX(46px) scale(0.96); }
  100% { opacity: 0.3; transform: translateX(62px) scale(0.88); }
`;

const pulseArrow = keyframes`
  0%, 100% { transform: translateX(0); opacity: 0.4; }
  50% { transform: translateX(5px); opacity: 1; }
`;

function CopyYearPreviewStage({ accent, fromYear, toYear }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 84,
        mt: 1.35,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: alpha(accent, 0.1),
        border: "1px solid",
        borderColor: alpha(accent, 0.22),
        animation: `${rise} 320ms ease both`,
        ...motionSafe,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 14,
          top: 14,
          width: 52,
          height: 56,
          borderRadius: 1.5,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(accent, 0.35),
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.15,
          boxShadow: `0 4px 12px ${alpha(accent, 0.12)}`,
          animation: `${bob} 2.2s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <CalendarMonthOutlinedIcon
          sx={{ fontSize: 20, color: alpha(accent, 0.7) }}
        />
        <Typography
          sx={{
            fontSize: "0.62rem",
            fontWeight: 900,
            color: accent,
            letterSpacing: -0.2,
          }}
        >
          {fromYear || "····"}
        </Typography>
      </Box>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: 58,
            top: 22 + (i % 2) * 8,
            width: 18,
            height: 22,
            borderRadius: 0.75,
            bgcolor: "#fff",
            border: "1.5px solid",
            borderColor: alpha(accent, 0.45),
            boxShadow: `0 4px 10px ${alpha(accent, 0.16)}`,
            animation: `${flyCopy} 2.8s ease-in-out infinite`,
            animationDelay: `${i * 0.16}s`,
            ...motionSafe,
          }}
        />
      ))}
      <Box
        sx={{
          position: "absolute",
          left: "48%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          color: accent,
          animation: `${pulseArrow} 1.5s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <ArrowForwardRoundedIcon sx={{ fontSize: 26 }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: 14,
          top: 14,
          width: 52,
          height: 56,
          borderRadius: 1.5,
          bgcolor: "#fff",
          border: "2px solid",
          borderColor: accent,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.15,
          boxShadow: `0 6px 16px ${alpha(accent, 0.25)}`,
          animation: `${bob} 2.2s ease-in-out infinite`,
          animationDelay: "0.25s",
          ...motionSafe,
        }}
      >
        <ContentCopyIcon sx={{ fontSize: 18, color: accent }} />
        <Typography
          sx={{
            fontSize: "0.62rem",
            fontWeight: 900,
            color: accent,
            letterSpacing: -0.2,
          }}
        >
          {toYear || "····"}
        </Typography>
      </Box>
    </Box>
  );
}
const INK = "#1c1917";
const DRAG_MIME = "application/x-design-calendar-nota";
/** Misma altura de fila en todos los meses → Por hacer alineado. */
const DAY_ROW_PX = 36;

function apiMessage(error, fallback) {
  return error?.response?.data?.error || fallback;
}

function ClientePicker({ value, onChange, labels, disabled }) {
  const catalog = useDesignCalendarClientes();
  const options = useMemo(
    () => pickerOptionsForValue(catalog, value),
    [catalog, value],
  );
  const selected =
    options.find((item) => item.id === (value || "otros")) ||
    clienteById(value || "otros", catalog);

  return (
    <Autocomplete
      size="small"
      disabled={disabled}
      options={options}
      value={selected}
      onChange={(_event, next) => {
        if (next?.id) onChange(next.id);
      }}
      getOptionLabel={(option) => option?.label || ""}
      isOptionEqualToValue={(a, b) => a?.id === b?.id}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          key={option.id}
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              bgcolor: option.fill,
              border: "2px solid",
              borderColor: option.color,
              flexShrink: 0,
            }}
          />
          <span>{option.label}</span>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={labels.client || "Cliente"}
          placeholder={labels.clientSearch || "Buscar marca…"}
        />
      )}
    />
  );
}

function SymbolPicker({ value = [], onChange, labels, disabled }) {
  const selected = Array.isArray(value) ? value : [];
  return (
    <Stack direction="row" spacing={0.55} useFlexGap flexWrap="wrap">
      {SIMBOLOS_PICKER.map((item) => {
        const on = selected.includes(item.id);
        return (
          <Tooltip key={item.id} title={labels[item.id] || item.id}>
            <Box
              component="button"
              type="button"
              disabled={disabled}
              aria-label={labels[item.id] || item.id}
              aria-pressed={on}
              onClick={() => {
                if (disabled) return;
                if (on) {
                  if (selected.length <= 1) return;
                  onChange(selected.filter((id) => id !== item.id));
                } else {
                  onChange([...selected, item.id].slice(0, 5));
                }
              }}
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                border: "1.5px solid",
                borderColor: on ? item.color : alpha(item.color, 0.22),
                bgcolor: alpha(item.color, on ? 0.22 : 0.08),
                color: item.color,
                cursor: disabled ? "default" : "pointer",
                opacity: disabled ? 0.55 : 1,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: on ? `0 0 0 3px ${alpha(item.color, 0.18)}` : "none",
                transition:
                  "background-color 0.15s ease, box-shadow 0.15s ease",
              }}
            >
              <DesignCalendarSymbolIcon id={item.id} size={24} />
            </Box>
          </Tooltip>
        );
      })}
    </Stack>
  );
}

function DayEditor({
  editor,
  notas,
  labels,
  canEdit,
  onClose,
  onCreate,
  onUpdate,
  onDelete,
}) {
  const [texto, setTexto] = useState("");
  const [simbolos, setSimbolos] = useState(["meeting"]);
  const [cliente, setCliente] = useState("todos");
  const [fechaFin, setFechaFin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setTexto("");
    setSimbolos(["meeting"]);
    setCliente("todos");
    setFechaFin(editor?.fecha || "");
    setError("");
  }, [editor?.fecha]);

  if (!editor) return null;

  const saveNew = async () => {
    if (!cliente) {
      setError(labels.clientRequired || "Indica el cliente");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await onCreate({
        fecha: editor.fecha,
        fechaFin: fechaFin || editor.fecha,
        texto,
        simbolos,
        simbolo: simbolos[0],
        cliente,
      });
      setTexto("");
      setSimbolos(["meeting"]);
      setCliente("todos");
      setFechaFin(editor.fecha);
    } catch (err) {
      setError(apiMessage(err, labels.saveError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover
      open
      anchorEl={editor.anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: "1px solid",
          borderColor: alpha(ACCENT, 0.2),
          boxShadow: "0 18px 48px rgba(28, 25, 23, 0.16)",
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          background: `linear-gradient(120deg, ${alpha(ACCENT, 0.16)} 0%, ${alpha("#c4a35a", 0.14)} 100%)`,
          borderBottom: "1px solid",
          borderColor: alpha(ACCENT, 0.12),
        }}
      >
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: "0.95rem",
            letterSpacing: -0.2,
            color: INK,
          }}
        >
          {formatDiaMes(editor.fecha)}
        </Typography>
        {!canEdit ? (
          <Typography sx={{ fontSize: "0.72rem", opacity: 0.7, mt: 0.2 }}>
            {labels.readOnly}
          </Typography>
        ) : null}
      </Box>
      <Stack spacing={1.1} sx={{ p: 1.4, width: 360 }}>
        {notas.map((nota) => (
          <NotaRow
            key={nota.Id}
            nota={nota}
            labels={labels}
            canEdit={canEdit}
            onUpdate={onUpdate}
            onDelete={onDelete}
          />
        ))}
        {canEdit ? (
          <>
            <TextField
              size="small"
              multiline
              minRows={2}
              value={texto}
              placeholder={labels.notePlaceholder}
              onChange={(event) => setTexto(event.target.value)}
            />
            <TextField
              size="small"
              type="date"
              label={labels.until}
              value={fechaFin || editor.fecha}
              onChange={(event) => setFechaFin(event.target.value)}
              inputProps={{ min: editor.fecha }}
              InputLabelProps={{ shrink: true }}
            />
            <ClientePicker
              value={cliente}
              onChange={setCliente}
              labels={labels}
            />
            <SymbolPicker
              value={simbolos}
              onChange={setSimbolos}
              labels={labels.symbols}
            />
            {error ? <Alert severity="error">{error}</Alert> : null}
            <Button
              variant="contained"
              disabled={busy || !texto.trim() || !simbolos.length || !cliente}
              onClick={saveNew}
              sx={{
                alignSelf: "flex-start",
                textTransform: "none",
                fontWeight: 800,
                bgcolor: ACCENT,
                borderRadius: 2,
                px: 1.5,
                "&:hover": { bgcolor: "#0d9488" },
              }}
            >
              {labels.addNote}
            </Button>
          </>
        ) : null}
      </Stack>
    </Popover>
  );
}

function NotaRow({ nota, labels, canEdit, onUpdate, onDelete }) {
  const [texto, setTexto] = useState(nota.Texto);
  const [simbolos, setSimbolos] = useState(notaSimbolos(nota));
  const [cliente, setCliente] = useState(nota.Cliente || "otros");
  const [fechaFin, setFechaFin] = useState(notaFechaFin(nota) || nota.Fecha);
  const [busy, setBusy] = useState(false);
  const catalog = useDesignCalendarClientes();
  const textDirty = texto.trim() !== nota.Texto;
  const fill = entryFillColor({ Cliente: cliente }, catalog);
  const accent = entryAccentColor({ Cliente: cliente }, catalog);

  useEffect(() => {
    setTexto(nota.Texto);
    setSimbolos(notaSimbolos(nota));
    setCliente(nota.Cliente || "otros");
    setFechaFin(notaFechaFin(nota) || nota.Fecha);
  }, [nota]);

  const persist = async (patch) => {
    if (!canEdit || busy) return;
    setBusy(true);
    try {
      await onUpdate(nota.Id, patch);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack
      spacing={0.65}
      sx={{
        p: 0.85,
        borderRadius: 2,
        bgcolor: fill,
        border: "1px solid",
        borderColor: alpha(accent, 0.35),
      }}
    >
      <Stack direction="row" spacing={0.5} alignItems="flex-start">
        <TextField
          size="small"
          fullWidth
          multiline
          value={texto}
          disabled={!canEdit}
          onChange={(event) => setTexto(event.target.value)}
          onBlur={() => {
            if (!canEdit || !textDirty || !texto.trim()) return;
            persist({ texto: texto.trim() });
          }}
        />
        {canEdit ? (
          <IconButton
            size="small"
            aria-label={labels.delete}
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onDelete(nota.Id);
              } finally {
                setBusy(false);
              }
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Stack>
      <TextField
        size="small"
        type="date"
        label={labels.until}
        value={fechaFin || nota.Fecha}
        disabled={!canEdit || busy}
        onChange={(event) => {
          const next = event.target.value;
          setFechaFin(next);
          persist({ fechaFin: next || nota.Fecha });
        }}
        inputProps={{ min: nota.Fecha }}
        InputLabelProps={{ shrink: true }}
      />
      <ClientePicker
        value={cliente}
        onChange={(next) => {
          setCliente(next);
          persist({ cliente: next });
        }}
        labels={labels}
        disabled={!canEdit || busy}
      />
      <SymbolPicker
        value={simbolos}
        onChange={(next) => {
          if (!next.length) return;
          setSimbolos(next);
          persist({ simbolos: next, simbolo: next[0] });
        }}
        labels={labels.symbols}
        disabled={!canEdit || busy}
      />
    </Stack>
  );
}

function MonthColumn({
  year,
  month,
  lang,
  notas = [],
  notasByFecha,
  acciones,
  labels,
  today,
  canEdit,
  focusAccionId,
  onOpenDay,
  onToggleHecho,
  onDeleteAccion,
  onCreateAccion,
  onDropNota,
}) {
  const theme = useTheme();
  const catalog = useDesignCalendarClientes();
  const name = monthName(year, month, lang);
  const length = daysInMonth(year, month);
  const accent = monthAccent(month);
  const [draftOpen, setDraftOpen] = useState(false);
  const [draftTexto, setDraftTexto] = useState("");
  const [draftFecha, setDraftFecha] = useState(toIsoDate(year, month, 1));
  const [draftCliente, setDraftCliente] = useState("todos");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dragOverFecha, setDragOverFecha] = useState(null);
  const segments = useMemo(
    () => layoutMonthNotas(notas, year, month),
    [notas, year, month],
  );
  const busyDays = useMemo(() => {
    let count = 0;
    for (let day = 1; day <= length; day += 1) {
      if ((notasByFecha.get(toIsoDate(year, month, day)) || []).length)
        count += 1;
    }
    return count;
  }, [notasByFecha, year, month, length]);

  useEffect(() => {
    setDraftFecha(toIsoDate(year, month, 1));
    setDraftTexto("");
    setDraftCliente("todos");
    setDraftOpen(false);
    setError("");
  }, [year, month]);

  const saveAccion = async () => {
    if (!draftCliente) {
      setError(labels.clientRequired || "Indica el cliente");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await onCreateAccion({
        texto: draftTexto,
        fechaAviso: draftFecha,
        cliente: draftCliente,
      });
      setDraftTexto("");
      setDraftCliente("todos");
      setDraftOpen(false);
    } catch (err) {
      setError(apiMessage(err, labels.saveError));
    } finally {
      setBusy(false);
    }
  };

  const pillShell = {
    width: "100%",
    minWidth: 0,
    borderRadius: 3,
    overflow: "hidden",
    bgcolor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.background.paper, 0.88)
        : "#fffcf8",
    border: "1px solid",
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 10px 28px rgba(0,0,0,0.28)"
        : `0 12px 32px ${alpha(accent, 0.1)}, 0 1px 0 ${alpha("#fff", 0.8)} inset`,
  };

  return (
    <Box
      id={`mes-${month}`}
      sx={{
        width: "100%",
        minWidth: 0,
        scrollMarginTop: 72,
        display: "flex",
        flexDirection: "column",
        gap: 1.1,
      }}
    >
      <Box
        sx={{
          ...pillShell,
          borderColor: alpha(accent, 0.28),
          display: "flex",
          flexDirection: "column",
          flex: "0 0 auto",
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 4,
            px: 0.9,
            py: 0.55,
            background: `linear-gradient(145deg, ${alpha(accent, 0.96)} 0%, ${alpha(accent, 0.84)} 55%, ${alpha(ACCENT, 0.78)} 100%)`,
            color: "#fff",
            boxShadow: `0 6px 14px ${alpha(accent, 0.22)}`,
          }}
        >
          <Stack
            direction="row"
            alignItems="baseline"
            justifyContent="space-between"
          >
            <Typography
              sx={{
                fontWeight: 900,
                fontSize: "0.78rem",
                letterSpacing: 0.25,
                textTransform: "uppercase",
              }}
            >
              {name}
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "0.62rem",
                opacity: 0.88,
              }}
            >
              {busyDays}/{length}
            </Typography>
          </Stack>
        </Box>

        <Box
          sx={{
            position: "relative",
            px: 0.35,
            pt: 0.35,
            pb: 0.35,
            height: 31 * DAY_ROW_PX + 6,
            flex: "0 0 auto",
            boxSizing: "content-box",
          }}
        >
          {Array.from({ length: 31 }, (_, index) => {
            const day = index + 1;
            if (day > length) {
              return <Box key={day} sx={{ height: DAY_ROW_PX }} />;
            }
            const fecha = toIsoDate(year, month, day);
            const dayNotas = notasByFecha.get(fecha) || [];
            const weekend = isWeekend(fecha);
            const isToday = fecha === today;
            const hasNotes = dayNotas.length > 0;
            const isDropTarget = canEdit && dragOverFecha === fecha;

            return (
              <Box
                key={fecha}
                onDragOver={(event) => {
                  if (!canEdit) return;
                  if (![...event.dataTransfer.types].includes(DRAG_MIME))
                    return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "move";
                  setDragOverFecha(fecha);
                }}
                onDragLeave={() => {
                  setDragOverFecha((prev) => (prev === fecha ? null : prev));
                }}
                onDrop={(event) => {
                  if (!canEdit) return;
                  event.preventDefault();
                  setDragOverFecha(null);
                  const raw =
                    event.dataTransfer.getData(DRAG_MIME) ||
                    event.dataTransfer.getData("text/plain");
                  const id = Number(raw);
                  if (!Number.isInteger(id) || id === 0) return;
                  onDropNota?.(id, fecha);
                }}
                sx={{
                  width: "100%",
                  height: DAY_ROW_PX,
                  boxSizing: "border-box",
                  px: 0.4,
                  display: "grid",
                  gridTemplateColumns: "18px 14px minmax(0, 1fr)",
                  columnGap: 0.4,
                  alignItems: "center",
                  cursor: "pointer",
                  borderRadius: 1.1,
                  // Sin relleno de cliente en la fila: las barras ya llevan el color
                  // (evita el “fantasma” rosa detrás que tapa lo de atrás).
                  bgcolor: isDropTarget
                    ? alpha(ACCENT, 0.22)
                    : isToday
                      ? alpha(accent, 0.12)
                      : weekend
                        ? alpha(theme.palette.text.primary, 0.035)
                        : "transparent",
                  outline: isDropTarget
                    ? `2px solid ${ACCENT}`
                    : isToday
                      ? `1.5px solid ${alpha(accent, 0.55)}`
                      : "none",
                  "&:hover": {
                    bgcolor: alpha(accent, 0.1),
                  },
                }}
                onClick={(event) => {
                  if (event.target.closest?.("[data-nota-bar]")) return;
                  onOpenDay({ fecha, anchorEl: event.currentTarget });
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onOpenDay({ fecha, anchorEl: event.currentTarget });
                  }
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: isToday || hasNotes ? 850 : 700,
                    fontVariantNumeric: "tabular-nums",
                    color: isToday ? accent : "inherit",
                  }}
                >
                  {day}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    opacity: weekend ? 0.45 : 0.58,
                    fontWeight: 750,
                    letterSpacing: 0.2,
                  }}
                >
                  {weekdayLetter(fecha, lang)}
                </Typography>
                <Box sx={{ minWidth: 0, height: "100%" }} />
              </Box>
            );
          })}

          <Box
            sx={{
              position: "absolute",
              left: 40,
              right: 4,
              top: 3,
              height: length * DAY_ROW_PX,
              pointerEvents: "none",
              zIndex: 2,
            }}
          >
            {segments.map((seg) => {
              const fill = entryFillColor(seg.nota, catalog);
              const barAccent = entryAccentColor(seg.nota, catalog);
              const ids = notaSimbolos(seg.nota);
              const span = notaSpanLabel(seg.nota);
              const laneCount = Math.max(1, seg.laneCount || 1);
              const lane = Number(seg.lane) || 0;
              const gapPct = 2;
              const widthPct = (100 - gapPct * (laneCount - 1)) / laneCount;
              const leftPct = lane * (widthPct + gapPct);
              return (
                <Box
                  key={`${seg.nota.Id}-${seg.startDay}-${seg.endDay}`}
                  data-nota-bar="1"
                  data-nota-drag={canEdit ? "1" : undefined}
                  draggable={canEdit}
                  onDragStart={(event) => {
                    if (!canEdit) return;
                    event.dataTransfer.setData(DRAG_MIME, String(seg.nota.Id));
                    event.dataTransfer.setData(
                      "text/plain",
                      String(seg.nota.Id),
                    );
                    event.dataTransfer.effectAllowed = "move";
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    const fecha = toIsoDate(year, month, seg.startDay);
                    onOpenDay({
                      fecha,
                      anchorEl: event.currentTarget,
                    });
                  }}
                  sx={{
                    position: "absolute",
                    top: (seg.startDay - 1) * DAY_ROW_PX + 2,
                    height: (seg.endDay - seg.startDay + 1) * DAY_ROW_PX - 4,
                    left: `${leftPct}%`,
                    width: `${widthPct}%`,
                    boxSizing: "border-box",
                    px: 0.45,
                    py: 0.2,
                    borderRadius: seg.continuesBefore
                      ? "0 8px 8px 0"
                      : seg.continuesAfter
                        ? "8px 0 0 8px"
                        : 1.5,
                    bgcolor: fill,
                    border: "1.5px solid",
                    borderColor: alpha(barAccent, 0.55),
                    borderLeftWidth: seg.continuesBefore ? 0 : 1.5,
                    borderRightWidth: seg.continuesAfter ? 0 : 1.5,
                    boxShadow: `0 2px 8px ${alpha(barAccent, 0.18)}`,
                    color: INK,
                    overflow: "hidden",
                    pointerEvents: "auto",
                    cursor: canEdit ? "grab" : "pointer",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 0.35,
                    zIndex: 2 + lane,
                    "&:active": {
                      cursor: canEdit ? "grabbing" : "pointer",
                    },
                    "&:hover": {
                      filter: "brightness(0.97)",
                      zIndex: 20,
                    },
                  }}
                >
                  {canEdit ? (
                    <DragIndicatorIcon
                      sx={{
                        fontSize: 13,
                        opacity: 0.4,
                        flexShrink: 0,
                        mt: "1px",
                      }}
                    />
                  ) : null}
                  <DesignCalendarSymbolStack ids={ids} size={15} max={3} />
                  <Typography
                    sx={{
                      fontSize: "0.68rem",
                      fontWeight: 750,
                      lineHeight: 1.25,
                      minWidth: 0,
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: Math.max(
                        1,
                        seg.endDay - seg.startDay + 1,
                      ),
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {seg.nota.Texto}
                    {span ? (
                      <Box
                        component="span"
                        sx={{
                          ml: 0.35,
                          opacity: 0.55,
                          fontSize: "0.58rem",
                          fontWeight: 700,
                        }}
                      >
                        {span}
                      </Box>
                    ) : null}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          ...pillShell,
          borderColor: alpha(ACCENT, 0.28),
          background:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.background.paper, 0.88)
              : `linear-gradient(165deg, ${alpha(ACCENT, 0.08)} 0%, #fffcf8 48%, #f8faf9 100%)`,
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 10px 28px rgba(0,0,0,0.28)"
              : `0 12px 28px ${alpha(ACCENT, 0.1)}, 0 1px 0 ${alpha("#fff", 0.85)} inset`,
          flex: "0 0 auto",
        }}
      >
        <Box
          sx={{
            px: 0.9,
            py: 0.5,
            background: `linear-gradient(145deg, ${alpha("#134e4a", 0.96)} 0%, ${alpha(ACCENT, 0.92)} 100%)`,
            color: "#fff",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.68rem",
              fontWeight: 900,
              letterSpacing: 0.7,
              textTransform: "uppercase",
            }}
          >
            {labels.future}
          </Typography>
        </Box>
        <Box sx={{ px: 0.75, pt: 0.75, pb: 0.85 }}>
          <Stack spacing={0.5}>
            {acciones.map((accion) => {
              const clienteColor = entryAccentColor(accion, catalog);
              const fill = entryFillColor(accion, catalog);
              return (
                <Stack
                  key={accion.Id}
                  id={`accion-${accion.Id}`}
                  direction="row"
                  spacing={0.15}
                  alignItems="flex-start"
                  sx={{
                    px: 0.35,
                    py: 0.25,
                    borderRadius: 1.25,
                    scrollMarginTop: 96,
                    bgcolor:
                      Number(focusAccionId) === Number(accion.Id)
                        ? alpha("#ff5a00", 0.28)
                        : accion.Hecho
                          ? alpha(fill, 0.35)
                          : fill,
                    outline:
                      Number(focusAccionId) === Number(accion.Id)
                        ? "2px solid #ff5a00"
                        : "none",
                    transition:
                      "background-color 0.25s ease, outline-color 0.25s ease",
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={Boolean(accion.Hecho)}
                    disabled={!canEdit}
                    onChange={(event) =>
                      onToggleHecho(accion, event.target.checked)
                    }
                    inputProps={{ "aria-label": labels.done }}
                    sx={{
                      p: 0.2,
                      color: clienteColor,
                      "&.Mui-checked": { color: clienteColor },
                    }}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 750,
                        lineHeight: 1.3,
                        textDecoration: accion.Hecho ? "line-through" : "none",
                        opacity: accion.Hecho ? 0.5 : 1,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          color: clienteColor,
                          mr: 0.35,
                          display: "inline-flex",
                          verticalAlign: "middle",
                        }}
                      >
                        <DesignCalendarSymbolIcon id="inicio" size={16} />
                      </Box>
                      {accion.Texto}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.6rem",
                        opacity: 0.58,
                        fontWeight: 650,
                      }}
                    >
                      {formatDiaMes(accion.FechaAviso)}
                      {" · "}
                      {clienteById(accion.Cliente, catalog).label}
                    </Typography>
                  </Box>
                  {canEdit ? (
                    <IconButton
                      size="small"
                      aria-label={labels.delete}
                      onClick={() => onDeleteAccion(accion.Id)}
                      sx={{ p: 0.2 }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  ) : null}
                </Stack>
              );
            })}
            {canEdit && draftOpen ? (
              <Stack spacing={0.6}>
                <TextField
                  size="small"
                  multiline
                  minRows={2}
                  value={draftTexto}
                  placeholder={labels.actionPlaceholder}
                  onChange={(event) => setDraftTexto(event.target.value)}
                />
                <TextField
                  size="small"
                  type="date"
                  value={draftFecha}
                  onChange={(event) => setDraftFecha(event.target.value)}
                  inputProps={{
                    min: toIsoDate(year, month, 1),
                    max: toIsoDate(year, month, length),
                  }}
                />
                <ClientePicker
                  value={draftCliente}
                  onChange={setDraftCliente}
                  labels={labels}
                />
                {error ? <Alert severity="error">{error}</Alert> : null}
                <Stack direction="row" spacing={0.5}>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={busy || !draftTexto.trim() || !draftCliente}
                    onClick={saveAccion}
                    sx={{
                      textTransform: "none",
                      fontWeight: 800,
                      bgcolor: accent,
                      "&:hover": { bgcolor: accent },
                    }}
                  >
                    {labels.addAction}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setDraftOpen(false)}
                    sx={{ textTransform: "none" }}
                  >
                    {labels.close}
                  </Button>
                </Stack>
              </Stack>
            ) : canEdit ? (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setDraftOpen(true)}
                sx={{
                  alignSelf: "flex-start",
                  textTransform: "none",
                  color: accent,
                  fontWeight: 800,
                  px: 0.4,
                }}
              >
                {labels.addAction}
              </Button>
            ) : null}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}

export default function DesignCalendarPage() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const { hash } = location;
  const { contentMargin } = useContext(SidebarContext);
  const today = todayIso();
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [notas, setNotas] = useState([]);
  const [acciones, setAcciones] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [fromExcel, setFromExcel] = useState(false);
  const [editor, setEditor] = useState(null);
  const [focusAccionId, setFocusAccionId] = useState(null);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyFrom, setCopyFrom] = useState(() => new Date().getFullYear());
  const [copyTo, setCopyTo] = useState(() => new Date().getFullYear() + 1);
  const [copyBusy, setCopyBusy] = useState(false);
  const [copyError, setCopyError] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [clientes, setClientes] = useState(DESIGN_CALENDAR_CLIENTES_DEFAULT);
  const [filterClientes, setFilterClientes] = useState([]);
  const scrolledHashRef = useRef("");
  const {
    isOnline: offlineOnline,
    pendingCount: offlinePendingCount,
    syncing: offlineSyncing,
    connectivityNotice: offlineNotice,
    setConnectivityNotice: setOfflineNotice,
    cacheSnapshot,
    loadCachedYear,
    hasPending: hasOfflinePending,
    queueOp,
    shouldQueue,
    flushPending,
  } = useDesignCalendarOffline(year);
  const offline = {
    isOnline: offlineOnline,
    pendingCount: offlinePendingCount,
    syncing: offlineSyncing,
    connectivityNotice: offlineNotice,
    setConnectivityNotice: setOfflineNotice,
    cacheSnapshot,
    loadCachedYear,
    hasPending: hasOfflinePending,
    queueOp,
    shouldQueue,
    flushPending,
  };
  const notasRef = useRef(notas);
  const accionesRef = useRef(acciones);
  const clientesRef = useRef(clientes);
  const canEditRef = useRef(canEdit);
  notasRef.current = notas;
  accionesRef.current = acciones;
  clientesRef.current = clientes;
  canEditRef.current = canEdit;

  const persistCache = useCallback(
    async (patch = {}) => {
      await cacheSnapshot(year, {
        notas: patch.notas ?? notasRef.current,
        acciones: patch.acciones ?? accionesRef.current,
        clientes: patch.clientes ?? clientesRef.current,
        canEdit: patch.canEdit ?? canEditRef.current,
        anio: year,
      });
    },
    [cacheSnapshot, year],
  );

  const labels = useMemo(
    () => ({
      addNote: t("designCalendar.addNote", "Añadir nota"),
      notePlaceholder: t(
        "designCalendar.notePlaceholder",
        "Meeting, shopping, viaje…",
      ),
      save: t("designCalendar.save", "Guardar"),
      delete: t("designCalendar.delete", "Borrar"),
      close: t("designCalendar.close", "Cerrar"),
      future: t("designCalendar.future", "Por hacer"),
      addAction: t("designCalendar.addAction", "Añadir arranque"),
      actionPlaceholder: t(
        "designCalendar.actionPlaceholder",
        "Inicio diseño Zara Woman SS27",
      ),
      done: t("designCalendar.done", "Hecho"),
      saveError: t("designCalendar.saveError", "No se ha podido guardar"),
      until: t("designCalendar.until", "Hasta"),
      client: t("designCalendar.client", "Marca"),
      clientSearch: t("designCalendar.clientSearch", "Buscar marca…"),
      clientRequired: t(
        "designCalendar.clientRequired",
        "Indica la marca (o Todos)",
      ),
      filterClientes: t("designCalendar.filterClientes", "Filtrar marcas"),
      filterClientesAll: t(
        "designCalendar.filterClientesAll",
        "Todas las marcas",
      ),
      copyYear: t("designCalendar.copyYear", "Copiar año"),
      copyYearTitle: t("designCalendar.copyYearTitle", "Copiar año completo"),
      copyYearHint: t(
        "designCalendar.copyYearHint",
        "Copia notas y arranques al año destino (debe estar vacío).",
      ),
      copyYearFrom: t("designCalendar.copyYearFrom", "Desde"),
      copyYearTo: t("designCalendar.copyYearTo", "Hacia"),
      copyYearOk: t("designCalendar.copyYearOk", "Copiar"),
      deleteYear: t("designCalendar.deleteYear", "Eliminar año"),
      readOnly: t(
        "designCalendar.readOnly",
        "Solo lectura — editan Agustina y Miguel",
      ),
      symbols: {
        meeting: t("designCalendar.symbolMeeting", "Meeting"),
        shopping: t("designCalendar.symbolShopping", "Shopping"),
        viaje: t("designCalendar.symbolViaje", "Viajes"),
        china: t("designCalendar.symbolChina", "China"),
        feria: t("designCalendar.symbolFeria", "Feria tejido"),
        nota: t("designCalendar.symbolNota", "Nota"),
        festival: t("designCalendar.symbolFestival", "Festivo"),
        inicio: t("designCalendar.symbolInicio", "Inicio"),
      },
    }),
    [t],
  );

  const applyYearData = useCallback((data) => {
    const nextNotas = Array.isArray(data?.notas) ? data.notas : [];
    const nextAcciones = Array.isArray(data?.acciones) ? data.acciones : [];
    const nextCanEdit = Boolean(data?.canEdit);
    setNotas(nextNotas);
    setAcciones(nextAcciones);
    setCanEdit(nextCanEdit);
    if (Array.isArray(data?.clientes) && data.clientes.length) {
      setClientes(mergeClientesCatalog(data.clientes));
    }
    return {
      notas: nextNotas,
      acciones: nextAcciones,
      clientes: Array.isArray(data?.clientes)
        ? mergeClientesCatalog(data.clientes)
        : clientesRef.current,
      canEdit: nextCanEdit,
    };
  }, []);

  const load = useCallback(
    async ({ silent = false, skipIfPending = false } = {}) => {
      if (!silent) {
        setLoading(true);
        setError("");
        setFromExcel(false);
      }
      try {
        if (skipIfPending && (await hasOfflinePending(year))) {
          return;
        }
        if (!offlineOnline && silent) {
          const cached = await loadCachedYear(year);
          if (cached) applyYearData(cached);
          return;
        }
        const data = await fetchDesignCalendar(year);
        const applied = applyYearData(data);
        await cacheSnapshot(
          year,
          { ...applied, anio: year },
          {
            keepPending: true,
          },
        );
        if (silent) setFromExcel(false);
      } catch (err) {
        const cached = await loadCachedYear(year);
        if (cached) {
          applyYearData(cached);
          setOfflineNotice(
            isNetworkError(err) || !offlineOnline ? "offline" : "pending",
          );
          if (silent) return;
          setFromExcel(false);
          return;
        }
        if (silent) return;
        if (year === DESIGN_CALENDAR_SEED_YEAR) {
          setNotas(designCalendarSeedNotas);
          setAcciones(designCalendarSeedAcciones);
          setFromExcel(true);
          setCanEdit(false);
        } else {
          setError(
            apiMessage(
              err,
              t(
                "designCalendar.loadError",
                "No se ha podido cargar el calendario",
              ),
            ),
          );
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [
      year,
      t,
      applyYearData,
      hasOfflinePending,
      offlineOnline,
      loadCachedYear,
      cacheSnapshot,
      setOfflineNotice,
    ],
  );

  useEffect(() => {
    load();
  }, [load]);

  // Sync entre PCs: socket inmediato + poll suave (sin spinner).
  // Con cola offline pendiente no pisamos el estado local.
  useEffect(() => {
    let cancelled = false;
    let debounceTimer = null;
    const softReload = () => {
      if (cancelled) return;
      if (debounceTimer) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        load({ silent: true, skipIfPending: true }).catch(() => {});
      }, 280);
    };

    const onSocket = (payload) => {
      const anio = Number(payload?.anio);
      if (Number.isInteger(anio) && anio > 0 && anio !== Number(year)) return;
      softReload();
    };

    socket.on("design-calendar-updated", onSocket);

    const pollId = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      softReload();
    }, 20000);

    const onVisible = () => {
      if (document.visibilityState === "visible") softReload();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      if (debounceTimer) window.clearTimeout(debounceTimer);
      window.clearInterval(pollId);
      socket.off("design-calendar-updated", onSocket);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load, year]);

  // Al volver online: flush cola y recarga limpia.
  useEffect(() => {
    if (!offlineOnline) return undefined;
    let cancelled = false;
    (async () => {
      const result = await flushPending(year);
      if (cancelled) return;
      if (result?.ok || result?.flushed) {
        await load({ silent: true });
      }
    })().catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [offlineOnline, year, flushPending, load]);

  useEffect(() => {
    setEditor(null);
  }, [year]);

  useEffect(() => {
    const nextYear = Number(location.state?.designCalendarYear);
    if (Number.isInteger(nextYear) && nextYear >= 2000 && nextYear <= 2100) {
      setYear((prev) => (prev === nextYear ? prev : nextYear));
    }
  }, [location.state, hash]);

  // Solo posiciona al llegar desde el banner (una vez por hash/key), no en cada soft-reload.
  useEffect(() => {
    if (loading) return;
    const id = hash.replace("#", "");
    if (!id) {
      scrolledHashRef.current = "";
      return undefined;
    }
    const scrollKey = `${location.key}|${id}|${year}`;
    if (scrolledHashRef.current === scrollKey) return undefined;

    const accionMatch = id.match(/^accion-(\d+)$/);
    if (accionMatch) setFocusAccionId(Number(accionMatch[1]));

    const scrollToTarget = () => {
      const el = document.getElementById(id);
      if (!el) return false;
      scrolledHashRef.current = scrollKey;
      el.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "center",
      });
      return true;
    };

    if (scrollToTarget()) return undefined;
    const t1 = window.setTimeout(scrollToTarget, 120);
    const t2 = window.setTimeout(scrollToTarget, 400);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [hash, loading, year, location.key]);

  useEffect(() => {
    if (focusAccionId == null) return undefined;
    const timer = window.setTimeout(() => setFocusAccionId(null), 2800);
    return () => window.clearTimeout(timer);
  }, [focusAccionId]);

  const filterIds = useMemo(
    () => filterClientes.map((item) => item.id),
    [filterClientes],
  );

  const filterOptions = useMemo(() => {
    const base = Array.isArray(clientes) ? [...clientes] : [];
    const seen = new Set(base.map((item) => item.id));
    for (const row of [...notas, ...acciones]) {
      const id = String(row?.Cliente || "")
        .trim()
        .toLowerCase();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      base.push(clienteById(id, clientes));
    }
    return base;
  }, [clientes, notas, acciones]);

  const filteredNotas = useMemo(() => {
    if (!filterIds.length) return notas;
    return notas.filter((item) =>
      matchesClienteFilter(item.Cliente, filterIds),
    );
  }, [notas, filterIds]);

  const filteredAcciones = useMemo(() => {
    if (!filterIds.length) return acciones;
    return acciones.filter((item) =>
      matchesClienteFilter(item.Cliente, filterIds),
    );
  }, [acciones, filterIds]);

  const notasByFecha = useMemo(
    () => groupNotasByFecha(filteredNotas),
    [filteredNotas],
  );
  const accionesByMonth = useMemo(
    () => groupAccionesByMonth(filteredAcciones),
    [filteredAcciones],
  );
  const editorNotas = useMemo(() => {
    if (!editor) return [];
    const list = notasByFecha.get(editor.fecha) || [];
    const seen = new Set();
    return list.filter((nota) => {
      if (seen.has(nota.Id)) return false;
      seen.add(nota.Id);
      return true;
    });
  }, [editor, notasByFecha]);
  const pendingStarts = filteredAcciones.filter((item) => !item.Hecho).length;

  const queueLocalNotaCreate = async (body) => {
    const tempId = nextTempId(notasRef.current.map((n) => n.Id));
    const local = {
      Id: tempId,
      Fecha: body.fecha,
      FechaFin: body.fechaFin || body.fecha,
      Texto: body.texto,
      Simbolo: body.simbolo || (body.simbolos && body.simbolos[0]) || "nota",
      Simbolos: body.simbolos || ["nota"],
      Cliente: body.cliente || "otros",
    };
    const next = [...notasRef.current, local].sort(
      (a, b) =>
        String(a.Fecha).localeCompare(String(b.Fecha)) ||
        Number(a.Id) - Number(b.Id),
    );
    setNotas(next);
    await offline.queueOp(year, {
      type: "nota.create",
      tempId,
      payload: body,
    });
    await persistCache({ notas: next });
  };

  const createNota = async (body) => {
    if (offline.shouldQueue()) {
      await queueLocalNotaCreate(body);
      return;
    }
    try {
      const created = await createDesignCalendarNota(body);
      const next = [...notasRef.current, created].sort(
        (a, b) =>
          String(a.Fecha).localeCompare(String(b.Fecha)) ||
          Number(a.Id) - Number(b.Id),
      );
      setNotas(next);
      await persistCache({ notas: next });
    } catch (err) {
      if (isNetworkError(err)) {
        await queueLocalNotaCreate(body);
        return;
      }
      throw err;
    }
  };

  const updateNota = async (id, body) => {
    const applyLocal = async () => {
      const next = notasRef.current.map((item) => {
        if (Number(item.Id) !== Number(id)) return item;
        const patch = { ...item };
        if (body.texto != null) patch.Texto = body.texto;
        if (body.fecha != null) patch.Fecha = body.fecha;
        if (body.fechaFin != null) patch.FechaFin = body.fechaFin;
        if (body.targetFecha != null) {
          const start = String(item.Fecha || "").slice(0, 10);
          const end = notaFechaFin(item) || start;
          let spanDays = 0;
          let cursor = start;
          while (cursor && cursor < end && spanDays < 400) {
            cursor = addDaysIso(cursor, 1);
            spanDays += 1;
          }
          patch.Fecha = body.targetFecha;
          patch.FechaFin =
            addDaysIso(body.targetFecha, spanDays) || body.targetFecha;
        }
        if (body.cliente != null) patch.Cliente = body.cliente;
        if (body.simbolos != null) patch.Simbolos = body.simbolos;
        if (body.simbolo != null) patch.Simbolo = body.simbolo;
        return patch;
      });
      setNotas(next);
      await offline.queueOp(year, { type: "nota.update", id, payload: body });
      await persistCache({ notas: next });
    };

    if (offline.shouldQueue()) {
      await applyLocal();
      return;
    }
    try {
      const updated = await updateDesignCalendarNota(id, body);
      const next = notasRef.current.map((item) =>
        Number(item.Id) === Number(id) ? updated : item,
      );
      setNotas(next);
      await persistCache({ notas: next });
    } catch (err) {
      if (isNetworkError(err)) {
        await applyLocal();
        return;
      }
      throw err;
    }
  };

  const removeNota = async (id) => {
    const applyLocal = async () => {
      const next = notasRef.current.filter(
        (item) => Number(item.Id) !== Number(id),
      );
      if (editor?.fecha) {
        const still = next.some((nota) => {
          const start = String(nota.Fecha || "").slice(0, 10);
          const end = notaFechaFin(nota) || start;
          return editor.fecha >= start && editor.fecha <= end;
        });
        if (!still) setEditor(null);
      }
      setNotas(next);
      await offline.queueOp(year, { type: "nota.delete", id });
      await persistCache({ notas: next });
    };

    if (offline.shouldQueue()) {
      await applyLocal();
      return;
    }
    try {
      await deleteDesignCalendarNota(id);
      const next = notasRef.current.filter(
        (item) => Number(item.Id) !== Number(id),
      );
      if (editor?.fecha) {
        const still = next.some((nota) => {
          const start = String(nota.Fecha || "").slice(0, 10);
          const end = notaFechaFin(nota) || start;
          return editor.fecha >= start && editor.fecha <= end;
        });
        if (!still) setEditor(null);
      }
      setNotas(next);
      await persistCache({ notas: next });
    } catch (err) {
      if (isNetworkError(err)) {
        await applyLocal();
        return;
      }
      throw err;
    }
  };

  const runCopyYear = async () => {
    if (!offline.isOnline) {
      setCopyError(
        t(
          "designCalendar.offlineBlockCopy",
          "Necesitas conexión para copiar un año.",
        ),
      );
      return;
    }
    setCopyBusy(true);
    setCopyError("");
    try {
      await copyDesignCalendarYear(Number(copyFrom), Number(copyTo));
      setCopyOpen(false);
      if (Number(copyTo) === Number(year)) {
        await load({ silent: true });
      } else {
        setYear(Number(copyTo));
      }
    } catch (err) {
      setCopyError(apiMessage(err, labels.saveError));
    } finally {
      setCopyBusy(false);
    }
  };

  const runDeleteYear = async ({ anio, confirm, confirmAnio }) => {
    if (!offline.isOnline) {
      setDeleteError(
        t(
          "designCalendar.offlineBlockDelete",
          "Necesitas conexión para eliminar un año.",
        ),
      );
      return;
    }
    setDeleteBusy(true);
    setDeleteError("");
    try {
      await deleteDesignCalendarYear(anio, confirm, confirmAnio);
      setDeleteOpen(false);
      setNotas([]);
      setAcciones([]);
      await offline.cacheSnapshot(
        year,
        { notas: [], acciones: [], clientes: clientesRef.current, canEdit },
        { keepPending: false },
      );
      await load({ silent: true });
    } catch (err) {
      setDeleteError(apiMessage(err, labels.saveError));
    } finally {
      setDeleteBusy(false);
    }
  };

  const dropNota = async (id, targetFecha) => {
    const nota = notas.find((item) => Number(item.Id) === Number(id));
    if (!nota) return;
    if (String(nota.Fecha).slice(0, 10) === targetFecha) return;
    try {
      await updateNota(id, { targetFecha });
    } catch (err) {
      setError(apiMessage(err, labels.saveError));
    }
  };

  const createAccion = async (body) => {
    const queueLocal = async () => {
      const tempId = nextTempId(accionesRef.current.map((a) => a.Id));
      const fecha = body.fechaAviso || body.fecha;
      const local = {
        Id: tempId,
        Texto: body.texto,
        FechaAviso: fecha,
        Anio: Number(String(fecha).slice(0, 4)),
        Mes: Number(String(fecha).slice(5, 7)),
        Hecho: false,
        Cliente: body.cliente || "todos",
      };
      const next = [...accionesRef.current, local];
      setAcciones(next);
      await offline.queueOp(year, {
        type: "accion.create",
        tempId,
        payload: body,
      });
      await persistCache({ acciones: next });
    };
    if (offline.shouldQueue()) {
      await queueLocal();
      return;
    }
    try {
      const created = await createDesignCalendarAccion(body);
      const next = [...accionesRef.current, created];
      setAcciones(next);
      await persistCache({ acciones: next });
    } catch (err) {
      if (isNetworkError(err)) {
        await queueLocal();
        return;
      }
      throw err;
    }
  };

  const toggleHecho = async (accion, hecho) => {
    const applyLocal = async () => {
      const next = accionesRef.current.map((item) =>
        Number(item.Id) === Number(accion.Id)
          ? { ...item, Hecho: Boolean(hecho) }
          : item,
      );
      setAcciones(next);
      await offline.queueOp(year, {
        type: "accion.update",
        id: accion.Id,
        payload: { hecho },
      });
      await persistCache({ acciones: next });
    };
    if (offline.shouldQueue()) {
      await applyLocal();
      return;
    }
    try {
      const updated = await updateDesignCalendarAccion(accion.Id, { hecho });
      const next = accionesRef.current.map((item) =>
        Number(item.Id) === Number(accion.Id) ? updated : item,
      );
      setAcciones(next);
      await persistCache({ acciones: next });
    } catch (err) {
      if (isNetworkError(err)) {
        await applyLocal();
        return;
      }
      setError(apiMessage(err, labels.saveError));
    }
  };

  const removeAccion = async (id) => {
    const applyLocal = async () => {
      const next = accionesRef.current.filter(
        (item) => Number(item.Id) !== Number(id),
      );
      setAcciones(next);
      await offline.queueOp(year, { type: "accion.delete", id });
      await persistCache({ acciones: next });
    };
    if (offline.shouldQueue()) {
      await applyLocal();
      return;
    }
    try {
      await deleteDesignCalendarAccion(id);
      const next = accionesRef.current.filter(
        (item) => Number(item.Id) !== Number(id),
      );
      setAcciones(next);
      await persistCache({ acciones: next });
    } catch (err) {
      if (isNetworkError(err)) {
        await applyLocal();
        return;
      }
      setError(apiMessage(err, labels.saveError));
    }
  };

  const onClientesCatalogChange = async (nextCatalog) => {
    const merged = mergeClientesCatalog(nextCatalog);
    setClientes(merged);
    await persistCache({ clientes: merged });
  };

  const updateClienteOfflineAware = async (id, patch) => {
    const applyLocal = async () => {
      const merged = clientesRef.current.map((row) =>
        row.id === id ? { ...row, ...patch } : row,
      );
      setClientes(merged);
      await offline.queueOp(year, {
        type: "cliente.update",
        id,
        payload: patch,
      });
      await persistCache({ clientes: merged });
      return { clientes: merged };
    };
    if (offline.shouldQueue()) return applyLocal();
    try {
      const data = await updateDesignCalendarCliente(id, patch);
      if (Array.isArray(data?.clientes)) {
        const merged = mergeClientesCatalog(data.clientes);
        setClientes(merged);
        await persistCache({ clientes: merged });
      }
      return data;
    } catch (err) {
      if (isNetworkError(err)) return applyLocal();
      throw err;
    }
  };

  return (
    <DesignCalendarClientesContext.Provider value={clientes}>
      <Box
        sx={{
          ...contentMargin,
          px: { xs: 1.25, md: 2.5 },
          pt: 1,
          pb: { xs: 10, md: 2 },
          boxSizing: "border-box",
          height: { xs: "100dvh", md: "100vh" },
          maxHeight: { xs: "100dvh", md: "100vh" },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          position: "relative",
          background:
            theme.palette.mode === "dark"
              ? `radial-gradient(ellipse at top left, ${alpha(ACCENT, 0.18)} 0%, transparent 42%), ${theme.palette.background.default}`
              : `radial-gradient(ellipse at 12% 0%, ${alpha("#0f766e", 0.12)} 0%, transparent 40%),
               radial-gradient(ellipse at 88% 8%, ${alpha("#c4a35a", 0.14)} 0%, transparent 38%),
               linear-gradient(180deg, #f7f3ec 0%, #f3efe6 48%, #eef5f3 100%)`,
          "&::before": {
            content: '""',
            pointerEvents: "none",
            position: "absolute",
            inset: 0,
            opacity: theme.palette.mode === "dark" ? 0.04 : 0.035,
            backgroundImage:
              "repeating-linear-gradient(0deg, transparent, transparent 23px, rgba(28,25,23,0.35) 24px), repeating-linear-gradient(90deg, transparent, transparent 23px, rgba(28,25,23,0.28) 24px)",
            maskImage:
              "linear-gradient(180deg, rgba(0,0,0,0.55), transparent 88%)",
          },
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 20,
            flexShrink: 0,
            mb: 1,
            borderRadius: 2.5,
            overflow: "hidden",
            border: "1px solid",
            borderColor: alpha(ACCENT, 0.2),
            background:
              theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${alpha(ACCENT, 0.28)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 60%)`
                : "linear-gradient(135deg, #e7f4f1 0%, #f6f0e4 100%)",
            backdropFilter: "blur(10px)",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 6px 18px rgba(0,0,0,0.28)"
                : "0 8px 20px rgba(28, 25, 23, 0.08)",
            "&::before": {
              content: '""',
              display: "block",
              height: 3,
              background: `linear-gradient(90deg, ${ACCENT}, #c4a35a 55%, #db2777)`,
            },
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            useFlexGap
            flexWrap="wrap"
            sx={{ px: { xs: 1.1, md: 1.5 }, py: 0.85 }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ minWidth: 0 }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(ACCENT, 0.14),
                  color: ACCENT,
                  flexShrink: 0,
                }}
              >
                <CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  letterSpacing: -0.3,
                  color: theme.palette.mode === "dark" ? "#fafaf9" : INK,
                  whiteSpace: "nowrap",
                }}
              >
                {t("designCalendar.title", "Design Calendar")}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  color: ACCENT,
                  opacity: 0.85,
                  display: { xs: "none", sm: "block" },
                }}
              >
                {filteredNotas.length}
                {filterIds.length && filteredNotas.length !== notas.length
                  ? `/${notas.length}`
                  : ""}{" "}
                {t("designCalendar.statNotes", "notas")}
                {" · "}
                {pendingStarts} {t("designCalendar.statStarts", "arranques")}
                {!canEdit
                  ? ` · ${t("designCalendar.readOnlyShort", "solo lectura")}`
                  : ""}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.15}
                sx={{
                  px: 0.35,
                  py: 0.15,
                  borderRadius: 999,
                  bgcolor: alpha(ACCENT, 0.1),
                  border: "1px solid",
                  borderColor: alpha(ACCENT, 0.18),
                }}
              >
                <IconButton
                  size="small"
                  aria-label={t("designCalendar.prevYear", "Año anterior")}
                  onClick={() => setYear((value) => value - 1)}
                  sx={{ color: ACCENT, p: 0.35 }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <Typography
                  sx={{
                    fontWeight: 900,
                    minWidth: 48,
                    textAlign: "center",
                    fontSize: "0.95rem",
                    letterSpacing: -0.4,
                    color: theme.palette.mode === "dark" ? "#fff" : INK,
                  }}
                >
                  {year}
                </Typography>
                <IconButton
                  size="small"
                  aria-label={t("designCalendar.nextYear", "Año siguiente")}
                  onClick={() => setYear((value) => value + 1)}
                  sx={{ color: ACCENT, p: 0.35 }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Autocomplete
                multiple
                size="small"
                options={filterOptions}
                value={filterClientes}
                onChange={(_event, next) => setFilterClientes(next || [])}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                limitTags={2}
                sx={{
                  minWidth: { xs: "100%", sm: 260 },
                  maxWidth: { xs: "100%", sm: 360 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 999,
                    bgcolor: alpha(ACCENT, 0.04),
                  },
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      size="small"
                      label={option.label}
                      sx={{
                        fontWeight: 700,
                        bgcolor: option.fill,
                        border: "1px solid",
                        borderColor: alpha(option.color, 0.45),
                        color: INK,
                      }}
                    />
                  ))
                }
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    key={option.id}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        bgcolor: option.fill,
                        border: "2px solid",
                        borderColor: option.color,
                        flexShrink: 0,
                      }}
                    />
                    {option.label}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      filterClientes.length ? "" : labels.filterClientesAll
                    }
                    label={labels.filterClientes}
                  />
                )}
              />
              <DesignCalendarClientesPalette
                canEdit={canEdit}
                updateCliente={updateClienteOfflineAware}
                onCatalogChange={onClientesCatalogChange}
              />
              {canEdit ? (
                <Button
                  size="small"
                  startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                  disabled={!offline.isOnline}
                  title={
                    offline.isOnline
                      ? undefined
                      : t(
                          "designCalendar.offlineBlockCopy",
                          "Necesitas conexión para copiar un año.",
                        )
                  }
                  onClick={() => {
                    setCopyFrom(year);
                    setCopyTo(year + 1);
                    setCopyError("");
                    setCopyOpen(true);
                  }}
                  sx={{
                    textTransform: "none",
                    fontWeight: 800,
                    color: ACCENT,
                    border: "1px solid",
                    borderColor: alpha(ACCENT, 0.28),
                    borderRadius: 999,
                    px: 1.1,
                  }}
                >
                  {labels.copyYear}
                </Button>
              ) : null}
              {canEdit ? (
                <Button
                  size="small"
                  startIcon={
                    <DeleteOutlineOutlinedIcon sx={{ fontSize: 16 }} />
                  }
                  disabled={!offline.isOnline}
                  title={
                    offline.isOnline
                      ? undefined
                      : t(
                          "designCalendar.offlineBlockDelete",
                          "Necesitas conexión para eliminar un año.",
                        )
                  }
                  onClick={() => {
                    setDeleteError("");
                    setDeleteOpen(true);
                  }}
                  sx={{
                    textTransform: "none",
                    fontWeight: 800,
                    color: "#b91c1c",
                    border: "1px solid",
                    borderColor: alpha("#b91c1c", 0.3),
                    borderRadius: 999,
                    px: 1.1,
                  }}
                >
                  {labels.deleteYear}
                </Button>
              ) : null}
              <Stack direction="row" spacing={0.45} useFlexGap flexWrap="wrap">
                {SIMBOLOS_PICKER.map((item) => (
                  <Box
                    key={item.id}
                    title={labels.symbols[item.id]}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.45,
                      px: 0.75,
                      py: 0.35,
                      borderRadius: 999,
                      bgcolor: alpha(item.color, 0.1),
                      border: "1px solid",
                      borderColor: alpha(item.color, 0.2),
                      color: item.color,
                      fontSize: "0.7rem",
                      fontWeight: 800,
                    }}
                  >
                    <DesignCalendarSymbolIcon id={item.id} size={18} />
                    <Box
                      component="span"
                      sx={{ display: { xs: "none", md: "inline" } }}
                    >
                      {labels.symbols[item.id]}
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Stack>
          </Stack>
        </Box>

        {!offline.isOnline ||
        offline.syncing ||
        offline.pendingCount > 0 ||
        offline.connectivityNotice ? (
          <Alert
            severity={
              !offline.isOnline || offline.connectivityNotice === "offline"
                ? "warning"
                : "info"
            }
            sx={{ mb: 1, flexShrink: 0, position: "relative" }}
          >
            {!offline.isOnline || offline.connectivityNotice === "offline"
              ? t(
                  "designCalendar.offlineBanner",
                  "Sin conexión — los cambios se guardan aquí y se sincronizan al volver.",
                )
              : offline.syncing || offline.connectivityNotice === "syncing"
                ? t(
                    "designCalendar.offlineSyncing",
                    "Sincronizando cambios pendientes…",
                  )
                : t(
                    "designCalendar.offlinePending",
                    "{{count}} cambio(s) pendientes de sincronizar.",
                    { count: offline.pendingCount },
                  )}
          </Alert>
        ) : null}
        {error ? (
          <Alert
            severity="error"
            sx={{ mb: 1, flexShrink: 0, position: "relative" }}
          >
            {error}
          </Alert>
        ) : null}
        {fromExcel ? (
          <Alert
            severity="info"
            sx={{ mb: 1, flexShrink: 0, position: "relative" }}
          >
            {t(
              "designCalendar.excelLoaded",
              "Casillas del Excel de Agus. Se guardan cuando el calendario esté en el servidor.",
            )}
          </Alert>
        ) : null}

        {loading ? (
          <Stack
            alignItems="center"
            sx={{ py: 8, position: "relative", flex: 1 }}
          >
            <CircularProgress size={28} sx={{ color: ACCENT }} />
          </Stack>
        ) : (
          <Box
            sx={{
              position: "relative",
              flex: 1,
              minHeight: 0,
              overflowX: "auto",
              overflowY: "auto",
              pb: 1.25,
              mx: { xs: -1.25, md: -0.5 },
              px: { xs: 1.25, md: 0.5 },
              WebkitOverflowScrolling: "touch",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                alignItems: "flex-start",
                gap: 1.25,
                width: "max-content",
                minWidth: "max-content",
              }}
            >
              {Array.from({ length: 12 }, (_, index) => index + 1).map(
                (month) => (
                  <Box
                    key={month}
                    sx={{
                      width: 360,
                      minWidth: 360,
                      maxWidth: 360,
                      flex: "0 0 360px",
                    }}
                  >
                    <MonthColumn
                      year={year}
                      month={month}
                      lang={i18n.language}
                      notas={filteredNotas}
                      notasByFecha={notasByFecha}
                      acciones={accionesByMonth.get(month) || []}
                      labels={labels}
                      today={today}
                      canEdit={canEdit}
                      focusAccionId={focusAccionId}
                      onOpenDay={setEditor}
                      onToggleHecho={toggleHecho}
                      onDeleteAccion={removeAccion}
                      onCreateAccion={createAccion}
                      onDropNota={dropNota}
                    />
                  </Box>
                ),
              )}
            </Box>
          </Box>
        )}

        <DayEditor
          editor={editor}
          notas={editorNotas}
          labels={labels}
          canEdit={canEdit}
          onClose={() => setEditor(null)}
          onCreate={createNota}
          onUpdate={updateNota}
          onDelete={removeNota}
        />

        <DesignCalendarDeleteYearDialog
          open={deleteOpen}
          year={year}
          notasCount={notas.length}
          accionesCount={acciones.length}
          deleting={deleteBusy}
          error={deleteError}
          onClose={() => !deleteBusy && setDeleteOpen(false)}
          onConfirm={runDeleteYear}
        />

        <Dialog
          open={copyOpen}
          onClose={() => !copyBusy && setCopyOpen(false)}
          maxWidth="xs"
          fullWidth
          keepMounted={false}
          transitionDuration={160}
          slotProps={{
            backdrop: {
              sx: {
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                backgroundColor: "rgba(15, 23, 42, 0.45)",
              },
            },
          }}
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid",
              borderColor: "divider",
              animation: copyOpen ? `${rise} 320ms ease both` : undefined,
              ...motionSafe,
            },
          }}
        >
          <Box
            sx={{
              px: 2.5,
              pt: 2,
              pb: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
              background: `linear-gradient(135deg, ${alpha(ACCENT, 0.14)} 0%, ${alpha(
                "#c4a35a",
                0.08,
              )} 100%)`,
            }}
          >
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              spacing={1}
            >
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
                sx={{ minWidth: 0 }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: alpha(ACCENT, 0.16),
                    color: ACCENT,
                    flexShrink: 0,
                    animation: `${bob} 2.2s ease-in-out infinite`,
                    ...motionSafe,
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: 22 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 800, lineHeight: 1.15 }}
                  >
                    {labels.copyYearTitle}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", lineHeight: 1.4, mt: 0.3 }}
                  >
                    {labels.copyYearHint}
                  </Typography>
                </Box>
              </Stack>
              <IconButton
                aria-label={labels.close}
                onClick={() => setCopyOpen(false)}
                disabled={copyBusy}
                size="small"
              >
                <CloseOutlinedIcon />
              </IconButton>
            </Stack>
            <CopyYearPreviewStage
              accent={ACCENT}
              fromYear={copyFrom}
              toYear={copyTo}
            />
          </Box>

          <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
            <Stack direction="row" spacing={1.5}>
              <TextField
                size="small"
                type="number"
                label={labels.copyYearFrom}
                value={copyFrom}
                onChange={(event) => setCopyFrom(event.target.value)}
                inputProps={{ min: 2000, max: 2100 }}
                fullWidth
                disabled={copyBusy}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    animation: `${rise} 340ms ease both`,
                    animationDelay: "60ms",
                    ...motionSafe,
                  },
                }}
              />
              <TextField
                size="small"
                type="number"
                label={labels.copyYearTo}
                value={copyTo}
                onChange={(event) => setCopyTo(event.target.value)}
                inputProps={{ min: 2000, max: 2100 }}
                fullWidth
                disabled={copyBusy}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    animation: `${rise} 340ms ease both`,
                    animationDelay: "120ms",
                    ...motionSafe,
                  },
                }}
              />
            </Stack>
            {copyError ? (
              <Alert severity="error" sx={{ mt: 1.5 }}>
                {copyError}
              </Alert>
            ) : null}
          </Box>

          <Stack
            direction="row"
            spacing={1.25}
            justifyContent="flex-end"
            sx={{
              px: 2.5,
              py: 1.75,
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: (th) => alpha(th.palette.background.default, 0.65),
            }}
          >
            <Button
              disabled={copyBusy}
              onClick={() => setCopyOpen(false)}
              sx={{ textTransform: "none", fontWeight: 700, minHeight: 44 }}
            >
              {labels.close}
            </Button>
            <Button
              variant="contained"
              disabled={copyBusy}
              onClick={runCopyYear}
              startIcon={
                copyBusy ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <ContentCopyIcon />
                )
              }
              sx={{
                textTransform: "none",
                fontWeight: 800,
                minHeight: 48,
                borderRadius: 2,
                bgcolor: ACCENT,
                boxShadow: `0 8px 18px ${alpha(ACCENT, 0.28)}`,
                "&:hover": { bgcolor: "#0d9488" },
              }}
            >
              {labels.copyYearOk}
            </Button>
          </Stack>
        </Dialog>
      </Box>
    </DesignCalendarClientesContext.Provider>
  );
}
